import { useState, useEffect, useCallback } from "react";
import { type LifecycleState, STATE_LABELS, STATE_COLORS } from "./lifecycle";
import { useModal } from "../ui/Modal";

interface BOMEntry {
  id: string;
  item_number: number;
  child_doc_id: string;
  part_number: string;
  name: string;
  revision: string;
  quantity: number;
  find_number: string;
  state: LifecycleState;
}

interface WhereUsedEntry {
  doc_id: string;
  name: string;
  part_number: string;
  revision: string;
  state: LifecycleState;
}

interface DocumentOption {
  id: string;
  name: string;
  part_number: string;
}

interface BOMPanelProps {
  docId: string;
  docType: string;
}

export default function BOMPanel({ docId, docType }: BOMPanelProps) {
  const modal = useModal();
  const [entries, setEntries] = useState<BOMEntry[]>([]);
  const [whereUsed, setWhereUsed] = useState<WhereUsedEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [showWhereUsed, setShowWhereUsed] = useState(false);
  const [whereUsedLoading, setWhereUsedLoading] = useState(false);
  const [showMultiLevel, setShowMultiLevel] = useState(false);
  const [multiLevelEntries, setMultiLevelEntries] = useState<any[]>([]);

  // Add form state
  const [docSearch, setDocSearch] = useState("");
  const [docOptions, setDocOptions] = useState<DocumentOption[]>([]);
  const [selectedDocId, setSelectedDocId] = useState("");
  const [addQty, setAddQty] = useState(1);
  const [addFindNumber, setAddFindNumber] = useState("");
  const [adding, setAdding] = useState(false);

  // Inline edit state
  const [editingQty, setEditingQty] = useState<string | null>(null);
  const [editQtyValue, setEditQtyValue] = useState(1);

  const fetchBom = useCallback(async () => {
    try {
      const res = await fetch(`/api/bom/${docId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load BOM");
      const data = await res.json();
      setEntries(data);
      setError("");
    } catch (err: any) {
      setError(err.message || "Failed to load BOM");
    } finally {
      setLoading(false);
    }
  }, [docId]);

  useEffect(() => {
    fetchBom();
  }, [fetchBom]);

  // Search documents for add form
  useEffect(() => {
    if (!docSearch.trim()) {
      setDocOptions([]);
      return;
    }
    const controller = new AbortController();
    fetch(`/api/documents?search=${encodeURIComponent(docSearch)}`, {
      credentials: "include",
      signal: controller.signal,
    })
      .then((res) => res.json())
      .then((data) => setDocOptions(data))
      .catch(() => {});
    return () => controller.abort();
  }, [docSearch]);

  const handleAdd = useCallback(async () => {
    if (!selectedDocId) return;
    setAdding(true);
    setError("");
    try {
      const res = await fetch(`/api/bom/${docId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          child_doc_id: selectedDocId,
          quantity: addQty,
          find_number: addFindNumber,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Add failed (${res.status})`);
      }
      setShowAddForm(false);
      setDocSearch("");
      setSelectedDocId("");
      setAddQty(1);
      setAddFindNumber("");
      await fetchBom();
    } catch (err: any) {
      setError(err.message || "Failed to add component");
    } finally {
      setAdding(false);
    }
  }, [docId, selectedDocId, addQty, addFindNumber, fetchBom]);

  const handleUpdateQty = useCallback(
    async (entryId: string, quantity: number) => {
      setError("");
      try {
        const res = await fetch(`/api/bom/${docId}/${entryId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ quantity }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || `Update failed (${res.status})`);
        }
        setEditingQty(null);
        await fetchBom();
      } catch (err: any) {
        setError(err.message || "Failed to update");
      }
    },
    [docId, fetchBom]
  );

  const handleDelete = useCallback(
    async (entryId: string) => {
      if (!(await modal.confirm("Remove this component from the BOM?", { destructive: true }))) return;
      setError("");
      try {
        const res = await fetch(`/api/bom/${docId}/${entryId}`, {
          method: "DELETE",
          credentials: "include",
        });
        if (!res.ok) throw new Error("Delete failed");
        await fetchBom();
      } catch (err: any) {
        setError(err.message || "Failed to delete");
      }
    },
    [docId, fetchBom]
  );

  const handleWhereUsedToggle = useCallback(async () => {
    if (showWhereUsed) {
      setShowWhereUsed(false);
      return;
    }
    setWhereUsedLoading(true);
    try {
      const res = await fetch(`/api/bom/${docId}/where-used`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load where-used");
      const data = await res.json();
      setWhereUsed(data);
      setShowWhereUsed(true);
    } catch (err: any) {
      setError(err.message || "Failed to load where-used");
    } finally {
      setWhereUsedLoading(false);
    }
  }, [docId, showWhereUsed]);

  const handleExportCsv = useCallback(() => {
    const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const header = "Item,Part Number,Name,Revision,Qty,State";
    const rows = entries.map(
      (e) =>
        `${e.item_number},${esc(e.part_number)},${esc(e.name)},${esc(e.revision)},${e.quantity},${esc(STATE_LABELS[e.state] || e.state)}`
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bom-${docId}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [entries, docId]);

  if (loading) {
    return (
      <div className="bom-panel">
        <div className="release-loading">Loading BOM...</div>
      </div>
    );
  }

  return (
    <div className="bom-panel">
      {/* Header actions */}
      <div className="bom-actions">
        <div className="release-section-header">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
            <rect x="2" y="2" width="12" height="12" rx="1" />
            <path d="M2 6h12M6 6v8" />
          </svg>
          Bill of Materials
        </div>
        <div className="bom-actions-buttons">
          <button
            className="bom-btn"
            onClick={() => setShowAddForm(!showAddForm)}
            title="Add Component"
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M8 3v10M3 8h10" />
            </svg>
            Add
          </button>
          <button
            className="bom-btn"
            onClick={handleExportCsv}
            disabled={entries.length === 0}
            title="Export CSV"
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M8 2v8M5 7l3 3 3-3M3 12h10" />
            </svg>
            CSV
          </button>
          <button
            className="bom-btn"
            onClick={handleWhereUsedToggle}
            title="Where Used"
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M8 2v4M4 6h8M4 6v4M12 6v4M4 10h2M10 10h2" />
            </svg>
            {whereUsedLoading ? "..." : showWhereUsed ? "BOM" : "Where Used"}
          </button>
          {docType === "assembly" && (
            <button
              className="bom-btn"
              onClick={async () => {
                if (!showMultiLevel) {
                  try {
                    const res = await fetch(`/api/bom/${docId}/multi-level`, { credentials: "include" });
                    if (res.ok) setMultiLevelEntries(await res.json());
                  } catch { /* ignore */ }
                }
                setShowMultiLevel(!showMultiLevel);
              }}
              title="Toggle multi-level tree"
            >
              {showMultiLevel ? "Flat" : "Tree"}
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && <div className="release-error">{error}</div>}

      {/* Add form */}
      {showAddForm && (
        <div className="bom-add-form">
          <div className="release-field">
            <label className="release-field-label">Document</label>
            <input
              className="release-field-input"
              type="text"
              placeholder="Search documents..."
              value={docSearch}
              onChange={(e) => {
                setDocSearch(e.target.value);
                setSelectedDocId("");
              }}
            />
          </div>
          {docOptions.length > 0 && !selectedDocId && (
            <div className="bom-doc-options">
              {docOptions.map((d) => (
                <button
                  key={d.id}
                  className="bom-doc-option"
                  onClick={() => {
                    setSelectedDocId(d.id);
                    setDocSearch(d.name || d.part_number || d.id);
                    setDocOptions([]);
                  }}
                >
                  <span className="bom-doc-option-name">{d.name}</span>
                  {d.part_number && (
                    <span className="bom-doc-option-pn">{d.part_number}</span>
                  )}
                </button>
              ))}
            </div>
          )}
          <div className="release-field">
            <label className="release-field-label">Quantity</label>
            <input
              className="release-field-input"
              type="number"
              min={1}
              value={addQty}
              onChange={(e) => setAddQty(Math.max(1, parseInt(e.target.value) || 1))}
            />
          </div>
          <div className="release-field">
            <label className="release-field-label">Find Number</label>
            <input
              className="release-field-input"
              type="text"
              placeholder="Optional"
              value={addFindNumber}
              onChange={(e) => setAddFindNumber(e.target.value)}
            />
          </div>
          <button
            className="release-save-btn"
            onClick={handleAdd}
            disabled={adding || !selectedDocId}
          >
            {adding ? "Adding..." : "Add Component"}
          </button>
        </div>
      )}

      {/* Multi-level tree view */}
      {showMultiLevel && multiLevelEntries.length > 0 && (
        <div className="bom-section">
          <div className="bom-section-title">Multi-Level BOM Tree</div>
          <table className="bom-table"><thead><tr>
            <th>Lvl</th><th>Item</th><th>Part Number</th><th>Name</th><th>Qty</th>
          </tr></thead><tbody>
            {multiLevelEntries.map((e: any, i: number) => (
              <tr key={i}>
                <td style={{ paddingLeft: (e.level ?? 0) * 16 }}>{e.level ?? 0}</td>
                <td>{e.item_number}</td>
                <td style={{ fontFamily: "var(--mono)", fontSize: 11 }}>{e.child_part_number}</td>
                <td>{e.child_name}</td>
                <td>{e.quantity}</td>
              </tr>
            ))}
          </tbody></table>
        </div>
      )}

      {/* Where Used view */}
      {showWhereUsed ? (
        <div className="bom-where-used">
          {whereUsed.length === 0 ? (
            <div className="version-empty">Not used in any assemblies</div>
          ) : (
            <table className="bom-table">
              <thead>
                <tr>
                  <th>Part Number</th>
                  <th>Name</th>
                  <th>Rev</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {whereUsed.map((w) => {
                  const colors = STATE_COLORS[w.state];
                  return (
                    <tr key={w.doc_id} className="bom-row">
                      <td className="bom-cell-pn">{w.part_number}</td>
                      <td>{w.name}</td>
                      <td>{w.revision}</td>
                      <td>
                        <span
                          className="bom-state-badge"
                          style={{ background: colors?.bg, color: colors?.fg }}
                        >
                          {STATE_LABELS[w.state] || w.state}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        /* BOM table */
        <div className="bom-table-wrap">
          {entries.length === 0 ? (
            <div className="version-empty">
              {docType === "assembly" ? "No components yet" : "BOM is only available for assemblies"}
            </div>
          ) : (
            <table className="bom-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Part Number</th>
                  <th>Name</th>
                  <th>Rev</th>
                  <th>Qty</th>
                  <th>State</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const colors = STATE_COLORS[e.state];
                  return (
                    <tr key={e.id} className="bom-row">
                      <td className="bom-cell-item">{e.item_number}</td>
                      <td className="bom-cell-pn">{e.part_number}</td>
                      <td>{e.name}</td>
                      <td>{e.revision}</td>
                      <td>
                        {editingQty === e.id ? (
                          <input
                            className="bom-qty-input"
                            type="number"
                            min={1}
                            value={editQtyValue}
                            autoFocus
                            onChange={(ev) =>
                              setEditQtyValue(Math.max(1, parseInt(ev.target.value) || 1))
                            }
                            onBlur={() => handleUpdateQty(e.id, editQtyValue)}
                            onKeyDown={(ev) => {
                              if (ev.key === "Enter") handleUpdateQty(e.id, editQtyValue);
                              if (ev.key === "Escape") setEditingQty(null);
                            }}
                          />
                        ) : (
                          <span
                            className="bom-qty-display"
                            onClick={() => {
                              setEditingQty(e.id);
                              setEditQtyValue(e.quantity);
                            }}
                            title="Click to edit"
                          >
                            {e.quantity}
                          </span>
                        )}
                      </td>
                      <td>
                        <span
                          className="bom-state-badge"
                          style={{ background: colors?.bg, color: colors?.fg }}
                        >
                          {STATE_LABELS[e.state] || e.state}
                        </span>
                      </td>
                      <td>
                        <button
                          className="bom-delete-btn"
                          onClick={() => handleDelete(e.id)}
                          title="Remove"
                        >
                          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
                            <path d="M4 4l8 8M12 4l-8 8" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
